import DOMPurify from "dompurify";
import { SquarePen, Trash2 } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { Button } from "@/app/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/app/components/ui/dialog";
import type { EducationItem } from "@/app/types/resume";

interface EducationSummaryBoxProps {
	item: EducationItem;
	index: number | string;
	deleteEduSummary: (referenceId: string | undefined) => void;
}

const EducationSummaryBox = ({ item, index, deleteEduSummary }: EducationSummaryBoxProps) => {
	const navigate = useNavigate();

	const eduInfoHandler = () => {
		navigate("/resume/education-information", {
			state: {
				referenceId: index,
			},
		});
	};
	const monthNames = [
		"January",
		"February",
		"March",
		"April",
		"May",
		"June",
		"July",
		"August",
		"September",
		"October",
		"November",
		"December",
	];

	const [showDeleteWarning, setShowDeleteWarning] = useState(false);

	return (
		<>
			<div className="mb-5 rounded-lg border border-black/10 p-5 font-inter box">
				<div className="flex flex-col gap-3 md:flex-row md:justify-between">
					<div className="md:w-3/4">
						<div className="mb-3">
							<h5 className="m-0 font-semibold text-base">
								{item.educationLevel}, {item.fieldOfStudy}
							</h5>
							<p className="text-sm">
								{item.schoolName} • {monthNames[Number(item.gMonth) - 1]} {item.gYear}
							</p>
						</div>
						<div
							// User-authored rich HTML — sanitize before injecting (stored-XSS guard).
							dangerouslySetInnerHTML={{
								__html: DOMPurify.sanitize(
									item.description.length > 350 ? `${item.description.slice(0, 300).trim()}...` : item.description,
								),
							}}
						/>
					</div>

					<div className="flex items-start justify-end gap-2">
						<button
							type="button"
							onClick={eduInfoHandler}
							className="flex min-w-max cursor-pointer items-center gap-1.5 rounded-lg border border-gray-100 px-2 py-2.5 text-sm leading-none"
						>
							<SquarePen className="size-4 text-[#7a7a7a]" /> Edit
						</button>
						<button
							type="button"
							onClick={() => setShowDeleteWarning(true)}
							className="flex cursor-pointer items-center rounded-lg border border-gray-100 px-2 py-2.5 leading-none"
						>
							<Trash2 className="size-4 text-red-600" />
							<span className="sr-only">Delete</span>
						</button>
					</div>
				</div>
			</div>
			<Dialog open={showDeleteWarning} onOpenChange={setShowDeleteWarning}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>You are about to delete this item</DialogTitle>
						<DialogDescription>Are you sure you want to delete this item?</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button type="button" variant="secondary" onClick={() => setShowDeleteWarning(false)}>
							Cancel
						</Button>
						<Button
							type="button"
							onClick={() => {
								deleteEduSummary(item.referenceId);
								setShowDeleteWarning(false);
							}}
						>
							Delete
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
};

export default EducationSummaryBox;
